// "Your Breathing Score in your phone" — recreates the core of flowty.co's
// "Flowty On Your Wrist" section scroll animation. Reverse-engineered from
// their actual shipped JS bundle (not guessed from watching it), because
// that pane's own screenshot compositing was broken this session too —
// read the source instead of eyeballing it.
//
// SIMPLIFIED 2026-08-26 per Martina: dropped the leader-line + individual
// feature-callout overlay (was fighting the design, and looked dashed
// instead of solid) in favor of a single pre-composed graphic that already
// has the callouts/lines/tagline baked in
// (assets/phone mockup.jpg — Martina's own higher-res re-export as of
// later the same day, replacing an earlier screenshot-quality version).
// What's left is just the core mechanic:
//
// 1. The photo starts as a tiny INLINE thumbnail sitting inside the
//    headline text itself, at #breathScoreSlot — an empty placeholder
//    span (sized in em so it scales with the heading) that reserves the
//    space but never renders content itself. The real photo (#breathScoreCard)
//    has no CSS width/height/top/left at all — its geometry is 100% JS-
//    driven, and starts out matching the slot's rect exactly, so at rest
//    it visually reads as "a small photo chip embedded in the sentence."
// 2. As the section scrolls into view, a one-shot entrance plays: the
//    headline's three word-groups fly in from three different directions
//    (left / right / rise-and-shrink) while the small photo reveals via a
//    clip-path iris wipe (small centered box -> full rounded rect).
// 3. Once the section's top reaches the top of the viewport, ScrollTrigger
//    pins it and scrubs a second timeline: the small revealed photo grows
//    from the slot's tiny size to fill the entire screen, while the
//    headline words scatter apart, fade, and blur away.
// 4. Independent of scroll: a subtle cursor-tilt on the photo (3D
//    rotate + small translate following the mouse within the card,
//    perspective set in CSS), snapping back smoothly on mouse-leave.
//
// BROUGHT TO MOBILE, THEN PULLED BACK OFF PHONES 2026-08-28, per Martina
// (kept as history, not deleted, per this file's own convention): this
// was desktop-only (>=961px, matching Flowty's own gating of this exact
// effect via a max-width:991px media check). First made it run at every
// width — see mobileCardTarget below for the "grow to a contained card
// instead of full-bleed" treatment that pass added, since chasing a
// literal 100vh while pinned fights mobile's dynamic viewport height
// (the browser chrome showing/hiding mid-scroll), the same category of
// problem the how-it-works section had. After seeing it live, she
// decided she prefers a plain static layout on phones specifically —
// she has a real vertical photo for that fallback now (see
// styles.css/index.html) — but still wants the animation on tablet and
// desktop. isPhone() below originally gated this off at 767px; still
// skipped entirely under prefers-reduced-motion regardless of width.
//
// WIDENED 2026-08-28 (second follow-up), per Martina: the plain static
// fallback should carry all the way through tablet too, not just phones —
// isPhone's cutoff moved from 767px to 950px, this pass's shared mobile/
// desktop line (matches cards-scroll.js, how-it-works.js, and
// device-render.js's own isMobile). isMobile() below moved to the same
// 950px value for the same reason: it only ever mattered for builds that
// get past isPhone's guard (picking the contained-card vs. full-bleed
// grow target), and now that isPhone/isMobile share one cutoff, nothing
// ever gets past the guard while still being "mobile" — mobileCardTarget
// and the mobile branches below are unreachable in practice now. Left in
// place rather than deleted, per this file's own convention above —
// genuinely desktop-only (>950px) is what Martina asked for here, with
// no separate narrow-tablet treatment any more.
//
// REVERTED 2026-08-29, per Martina: a same-day rewrite tried replacing
// the top/left/width/height tween below with a FLIP-style transform
// (translate+scale a box already set to its final size) to fix reported
// shakiness — four attempts at that rewrite all still rendered the photo
// wrong (oversized/distorted) at rest, for reasons that kept moving
// (immediateRender not applying inside a scrollTrigger timeline, a
// height measurement contaminated by DevTools eating viewport space, and
// finally a webfont-swap timing theory that also didn't fix it) — without
// a way to see the page directly, chasing it further risked burning more
// of her time for no result. This is the original, visually-correct
// geometry restored as-is; see .breath-score__card's own "contain" note
// in styles.css for the actual (much smaller, single-property) fix now
// aimed at the shakiness instead.
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

function initBreathScore() {
  const stage = document.getElementById("breathScoreStage");
  const slot = document.getElementById("breathScoreSlot");
  const card = document.getElementById("breathScoreCard");
  const cardTilt = document.getElementById("breathScoreCardTilt");
  const cardScale = document.getElementById("breathScoreCardScale");
  const wordYour = document.getElementById("bsWordYour");
  const wordScore = document.getElementById("bsWordScore");
  const wordPhone = document.getElementById("bsWordPhone");
  if (!stage || !slot || !card || !cardTilt || !cardScale || !wordYour || !wordScore || !wordPhone) {
    return;
  }

  // Gates whether the animation runs at all — phones get the plain
  // static fallback (.breath-score__mobile, styles.css). Separate from
  // isMobile below: isPhone is checked once per build() to bail out
  // entirely; isMobile (a wider 960px line, so it's really "phone or
  // tablet") only matters for builds that get past this check, i.e.
  // tablet only in practice, and just picks which grow target the
  // pin+scrub finale animates toward.
  const isPhone = () => window.matchMedia("(max-width: 950px)").matches;
  const isMobile = () => window.matchMedia("(max-width: 950px)").matches;

  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  // No reveal, no pin, no tilt — .breath-score__mobile (plain stacked
  // layout) is what's actually visible under reduced motion regardless of
  // viewport width; see the reduced-motion block in styles.css.
  if (reduceMotion) return;

  let revealTl, pinTl;

  // Card has no CSS geometry of its own — measured relative to its
  // position:relative ancestor (.breath-score__card-wrap), the same
  // coordinate space the pin/scrub tween writes top/left into.
  function slotRect() {
    const wrap = card.parentElement;
    const s = slot.getBoundingClientRect();
    const w = wrap.getBoundingClientRect();
    return {
      top: s.top - w.top,
      left: s.left - w.left,
      width: s.width,
      height: s.height,
    };
  }

  // Mobile's grow target — a centered, contained card, not full-bleed.
  // Deliberately NOT chasing window.innerHeight/vh as a percentage the
  // way the desktop target does (100vh) — that's exactly the value that
  // shifts mid-scroll as mobile browser chrome shows/hides. Capping both
  // dimensions in px, measured once at build time (rebuilt on resize,
  // same as slotRect's own coordinate space), means a few tens of
  // pixels of chrome resize just doesn't matter here the way it would
  // chasing a literal full screen fill. Proportions (86% wide / 72%
  // tall, capped) are a starting point for the current landscape
  // placeholder graphic — revisit once Martina's vertical photo is in,
  // since a real portrait image may want a taller/narrower cap.
  function mobileCardTarget() {
    const wrap = card.parentElement;
    const w = wrap.getBoundingClientRect();
    const width = Math.min(w.width * 0.86, 420);
    const height = Math.min(w.height * 0.72, 640);
    return {
      width,
      height,
      left: (w.width - width) / 2,
      top: (w.height - height) / 2,
    };
  }

  function killAll() {
    revealTl?.scrollTrigger?.kill();
    revealTl?.kill();
    revealTl = undefined;
    pinTl?.scrollTrigger?.kill();
    pinTl?.kill();
    pinTl = undefined;
  }

  function build() {
    killAll();
    // Matches cards-scroll.js/how-it-works.js's own pattern: re-checked
    // per build() (not just once at load), so resizing across the 950px
    // line switches modes correctly instead of getting stuck in
    // whichever one was active on page load. .breath-score__stage is
    // display:none below 951px in CSS regardless (see styles.css), but
    // this stops GSAP from ever pinning/measuring an element that's
    // about to be hidden, or that resize just hid.
    if (isPhone()) return;

    // Horizon's own base.css makes .page-wrapper — not window — the real
    // scrolling element at >=990px (html/body get overflow:hidden there,
    // see assets/base.css); ScrollTrigger has to be told that explicitly
    // or it measures against a container that never scrolls, which is
    // why this section silently did nothing on desktop while working
    // fine on mobile/tablet. Below 990px window is still the real
    // scroller, matching Horizon's own breakpoint exactly. Recomputed
    // per build() since this file already rebuilds on resize.
    const desktopScroller = window.matchMedia("(min-width: 990px)").matches
      ? ".page-wrapper"
      : undefined;

    gsap.set(card, { clipPath: "inset(50% 50% 50% 50% round 40px)" });
    gsap.set(wordYour, { opacity: 0, xPercent: -60 });
    gsap.set(wordScore, { opacity: 0, xPercent: 60 });
    gsap.set(wordPhone, { opacity: 0, yPercent: 40, scale: 1.3 });
    // At rest the image (object-fit: contain, so never cropped) is way
    // zoomed in on the phone+hand focal point (see .breath-score__card-
    // scale's transform-origin) — the callouts baked into the graphic
    // are illegible at slot size anyway, so only the recognizable part
    // shows. Scales back down to 1 (whole graphic) as the card grows.
    gsap.set(cardScale, { scale: 3 });

    // Entrance — plays once as the section approaches, resets if the user
    // scrolls back up past the start (same toggleActions Flowty uses, so
    // it replays on re-entry rather than staying stuck mid-animation).
    revealTl = gsap.timeline({
      scrollTrigger: {
        trigger: stage,
        start: "top 85%",
        toggleActions: "play none none reset",
        scroller: desktopScroller,
      },
    });
    revealTl
      .to(wordYour, { xPercent: 0, opacity: 1, duration: 1.2, ease: "expo.out" }, 0)
      .to(wordScore, { xPercent: 0, opacity: 1, duration: 1.2, ease: "expo.out" }, 0)
      .to(wordPhone, { yPercent: 0, opacity: 1, scale: 1, duration: 1.2, ease: "expo.out" }, 0)
      .to(card, { clipPath: "inset(0% 0% 0% 0% round 32px)", duration: 1, ease: "power4.out" }, 0);

    // Pin + scrub — fires later, once the section's top reaches the
    // viewport's top (by which point the entrance above has already
    // finished). Photo grows from the slot's tiny rect toward its grow
    // target (full-bleed on desktop, a contained card on mobile — see
    // mobileCardTarget above); headline scatters/blurs away over the
    // same range. Shorter scroll distance on mobile (+=100% vs +=150%)
    // since it's growing a much shorter distance (contained card, not
    // edge-to-edge) — the desktop distance made the growth feel
    // sluggish when tried at the mobile target during testing.
    const mobile = isMobile();
    pinTl = gsap.timeline({
      scrollTrigger: {
        trigger: stage,
        start: "top top",
        end: mobile ? "+=100%" : "+=150%",
        pin: true,
        scrub: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        scroller: desktopScroller,
        // Safety net: revealTl (above) is a real-time autoplay tween, not
        // scroll-scrubbed, so a fast scroll can reach the pin before it's
        // finished — the entrance and the scatter-out would then fight
        // over the same word-group properties. Forcing it to its end
        // state the instant the pin engages guarantees no overlap,
        // regardless of scroll speed.
        onEnter: () => revealTl?.progress(1),
        onEnterBack: () => revealTl?.progress(1),
      },
    });
    pinTl
      .fromTo(
        card,
        {
          top: () => slotRect().top,
          left: () => slotRect().left,
          width: () => slotRect().width,
          height: () => slotRect().height,
          immediateRender: true,
        },
        mobile
          ? {
              top: () => mobileCardTarget().top,
              left: () => mobileCardTarget().left,
              width: () => mobileCardTarget().width,
              height: () => mobileCardTarget().height,
              duration: 1,
              ease: "none",
            }
          : { top: 0, left: 0, width: "100%", height: "100vh", duration: 1, ease: "none" },
        0
      )
      .fromTo(cardScale, { scale: 3 }, { scale: 1, duration: 1, ease: "none" }, 0)
      // .fromTo() with explicit "from" values here, not .to() — a .to()
      // tween infers its start from whatever the property currently is at
      // first render, which for a scrub timeline is effectively page-load
      // time (before revealTl has run), locking in the wrong start value
      // and fighting with the entrance animation later. Explicit values
      // remove that ambiguity regardless of revealTl's own timing.
      .fromTo(wordYour, { xPercent: 0, opacity: 1, filter: "blur(0px)" }, { xPercent: -50, opacity: 0, filter: "blur(8px)", duration: 0.4, ease: "none" }, 0)
      .fromTo(wordScore, { xPercent: 0, opacity: 1, filter: "blur(0px)" }, { xPercent: 50, opacity: 0, filter: "blur(8px)", duration: 0.4, ease: "none" }, 0)
      .fromTo(wordPhone, { yPercent: 0, opacity: 1, scale: 1, filter: "blur(0px)" }, { yPercent: -40, scale: 1.4, opacity: 0, filter: "blur(8px)", duration: 0.4, ease: "none" }, 0);
  }

  build();

  // Console-inspectable state, same idea as window.__debugDevice in
  // device-render.js — lets progress be driven directly (tl.seek /
  // .progress()) without a screenshot or waiting on real scroll.
  window.__debugBreathScore = {
    get revealTl() {
      return revealTl;
    },
    get pinTl() {
      return pinTl;
    },
    isMobile,
    mobileCardTarget,
    slotRect,
    card,
  };

  // Cursor tilt on the photo — independent of scroll, mirrors Flowty's
  // mousemove/mouseleave tween on the same inner (preserve-3d) wrapper.
  function onMove(ev) {
    const rect = card.getBoundingClientRect();
    const dx = (ev.clientX - rect.left) / rect.width - 0.5;
    const dy = (ev.clientY - rect.top) / rect.height - 0.5;
    gsap.to(cardTilt, {
      x: dx * 24,
      y: dy * 24,
      rotateX: -dy * 4,
      rotateY: dx * 4,
      duration: 0.8,
      ease: "power2.out",
    });
  }
  function onLeave() {
    gsap.to(cardTilt, { x: 0, y: 0, rotateX: 0, rotateY: 0, duration: 1, ease: "power3.out" });
  }
  card.addEventListener("mousemove", onMove);
  card.addEventListener("mouseleave", onLeave);

  // Slot geometry shifts with viewport width (text reflow) and the
  // desktop/mobile split sits right at the 950px boundary — rebuild
  // rather than just refresh, same reasoning as cards-scroll.js.
  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(build, 150);
  });
}

initBreathScore();
